import emailjs from '@emailjs/browser';
import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { guardContentSubmission, showModerationWarning } from '../utils/moderation';
import './Contact.css';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_CONTACT_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const COPY = {
  home: '\u30db\u30fc\u30e0',
  pageTitle: '\u304a\u554f\u3044\u5408\u308f\u305b\u30fb\u30d0\u30b0\u5831\u544a',
  eyebrow: 'Support',
  pageDesc:
    '\u4e0d\u5177\u5408\u5831\u544a\u3001\u4f7f\u3044\u65b9\u306e\u8cea\u554f\u3001\u6539\u5584\u8981\u671b\u3092\u53d7\u3051\u4ed8\u3051\u3066\u3044\u307e\u3059\u3002' +
    '\u30d0\u30b0\u5831\u544a\u3067\u306f\u518d\u73fe\u624b\u9806\u307e\u3067\u66f8\u3044\u3066\u3082\u3089\u3048\u308b\u3068\u5207\u308a\u5206\u3051\u304c\u901f\u304f\u306a\u308a\u307e\u3059\u3002',
  inquiry: '\u304a\u554f\u3044\u5408\u308f\u305b',
  inquiryDesc: '\u3054\u610f\u898b\u3001\u3054\u8981\u671b\u3001\u4f7f\u3044\u65b9\u306b\u95a2\u3059\u308b\u8cea\u554f\u306f\u3053\u3061\u3089',
  bug: '\u30d0\u30b0\u5831\u544a',
  bugDesc: '\u4e0d\u5177\u5408\u3001\u8868\u793a\u5d29\u308c\u3001\u64cd\u4f5c\u4e0d\u80fd\u306a\u3069\u306f\u3053\u3061\u3089',
  thankYou:
    '\u9001\u4fe1\u3057\u307e\u3057\u305f\u3002\u5185\u5bb9\u3092\u78ba\u8a8d\u3057\u3001\u5fc5\u8981\u306b\u5fdc\u3058\u3066\u5bfe\u5fdc\u3057\u307e\u3059\u3002',
  sendAnother: '\u7d9a\u3051\u3066\u9001\u308b',
  subject: '\u4ef6\u540d',
  pageUrl: '\u767a\u751f\u30da\u30fc\u30b8 / \u95a2\u9023\u30da\u30fc\u30b8',
  name: '\u304a\u540d\u524d',
  email: '\u30e1\u30fc\u30eb\u30a2\u30c9\u30ec\u30b9',
  bugSteps: '\u518d\u73fe\u624b\u9806',
  expected: '\u671f\u5f85\u3057\u305f\u52d5\u4f5c',
  actual: '\u5b9f\u969b\u306e\u52d5\u4f5c',
  inquiryBody: '\u5185\u5bb9',
  bugBody: '\u88dc\u8db3\u60c5\u5831',
  captchaHint: '\u4eba\u9593\u78ba\u8a8d',
  sendFailed: '\u9001\u4fe1\u306b\u5931\u6557\u3057\u307e\u3057\u305f\u3002\u6642\u9593\u3092\u304a\u3044\u3066\u518d\u8a66\u884c\u3057\u3066\u304f\u3060\u3055\u3044\u3002',
  configMissing:
    '\u9001\u4fe1\u8a2d\u5b9a\u304c\u672a\u5b8c\u4e86\u3067\u3059\u3002\u7ba1\u7406\u8005\u3078\u8a2d\u5b9a\u8ffd\u52a0\u3092\u4f9d\u983c\u3057\u3066\u304f\u3060\u3055\u3044\u3002',
  captchaError: '\u78ba\u8a8d\u7528\u306e\u56de\u7b54\u304c\u4e00\u81f4\u3057\u307e\u305b\u3093\u3002',
  currentPath: '\u73fe\u5728\u306e\u30d1\u30b9',
  loggedIn: '\u30ed\u30b0\u30a4\u30f3\u4e2d',
  sending: '\u9001\u4fe1\u4e2d...',
  optional: '\u4efb\u610f',
  urlPlaceholder: 'https://...',
  captchaPlaceholder: '\u3072\u3089\u304c\u306a\u3067\u5165\u529b',
};

const CONTACT_TYPES = {
  inquiry: {
    label: COPY.inquiry,
    description: COPY.inquiryDesc,
    subjectPrefix: '[\u304a\u554f\u3044\u5408\u308f\u305b]',
    toolName: 'yamanoboro',
  },
  bug: {
    label: COPY.bug,
    description: COPY.bugDesc,
    subjectPrefix: '[\u30d0\u30b0\u5831\u544a]',
    toolName: 'yamanoboro',
  },
};

const CHALLENGES = [
  {
    question: '\u300c\u3084\u307e\u300d\u3092\u3072\u3089\u304c\u306a\u3067\u5165\u529b\u3057\u3066\u304f\u3060\u3055\u3044',
    answer: '\u3084\u307e',
  },
  {
    question: '\u300c\u3068\u3056\u3093\u300d\u3092\u3072\u3089\u304c\u306a\u3067\u5165\u529b\u3057\u3066\u304f\u3060\u3055\u3044',
    answer: '\u3068\u3056\u3093',
  },
  {
    question: '\u300c\u307f\u3061\u300d\u3092\u3072\u3089\u304c\u306a\u3067\u5165\u529b\u3057\u3066\u304f\u3060\u3055\u3044',
    answer: '\u307f\u3061',
  },
  {
    question: '\u300c\u305d\u3089\u300d\u3092\u3072\u3089\u304c\u306a\u3067\u5165\u529b\u3057\u3066\u304f\u3060\u3055\u3044',
    answer: '\u305d\u3089',
  },
  {
    question: '\u300c\u304b\u308f\u300d\u3092\u3072\u3089\u304c\u306a\u3067\u5165\u529b\u3057\u3066\u304f\u3060\u3055\u3044',
    answer: '\u304b\u308f',
  },
];

function pickChallenge() {
  return CHALLENGES[Math.floor(Math.random() * CHALLENGES.length)];
}

function buildStructuredMessage(data) {
  const lines = [
    `\u7a2e\u5225: ${CONTACT_TYPES[data.contactType].label}`,
    `\u9001\u4fe1\u5143URL: ${data.pageUrl || '\u672a\u5165\u529b'}`,
    `\u30ed\u30b0\u30a4\u30f3\u72b6\u614b: ${data.currentUser ? '\u30ed\u30b0\u30a4\u30f3\u4e2d' : '\u672a\u30ed\u30b0\u30a4\u30f3'}`,
    `\u30e6\u30fc\u30b6\u30fcUID: ${data.currentUser?.uid || '\u306a\u3057'}`,
  ];

  if (data.contactType === 'bug') {
    lines.push(`\u518d\u73fe\u624b\u9806: ${data.steps || '\u672a\u5165\u529b'}`);
    lines.push(`\u671f\u5f85\u3057\u305f\u52d5\u4f5c: ${data.expectedBehavior || '\u672a\u5165\u529b'}`);
    lines.push(`\u5b9f\u969b\u306e\u52d5\u4f5c: ${data.actualBehavior || '\u672a\u5165\u529b'}`);
  }

  lines.push('\u672c\u6587:');
  lines.push(data.message || '\u672a\u5165\u529b');
  return lines.join('\n');
}

function Contact() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [contactType, setContactType] = useState('inquiry');
  const [subject, setSubject] = useState('');
  const [name, setName] = useState(currentUser?.displayName || '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [message, setMessage] = useState('');
  const [pageUrl, setPageUrl] = useState(window.location.href);
  const [steps, setSteps] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [challenge, setChallenge] = useState(pickChallenge);
  const [captchaError, setCaptchaError] = useState(false);
  const [status, setStatus] = useState('idle');
  const [configError, setConfigError] = useState('');

  const selectedType = useMemo(() => CONTACT_TYPES[contactType], [contactType]);
  const isInquiry = contactType === 'inquiry';
  const isBugReport = contactType === 'bug';

  const resetAfterSuccess = () => {
    setSubject('');
    setMessage('');
    setSteps('');
    setExpectedBehavior('');
    setActualBehavior('');
    setCaptcha('');
    setCaptchaError(false);
    setStatus('idle');
    setConfigError('');
    setChallenge(pickChallenge());
  };

  const handleTypeChange = (nextType) => {
    setContactType(nextType);
    setSubject('');
    setMessage('');
    setSteps('');
    setExpectedBehavior('');
    setActualBehavior('');
    setConfigError('');
    setCaptchaError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!EMAILJS_SERVICE_ID || !EMAILJS_CONTACT_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setConfigError(COPY.configMissing);
      return;
    }

    if (!subject.trim() || !message.trim()) {
      return;
    }

    if (isInquiry && !email.trim()) {
      return;
    }

    if (isBugReport && (!steps.trim() || !actualBehavior.trim())) {
      return;
    }

    if (honeypot) {
      setStatus('done');
      return;
    }

    if (captcha.trim() !== challenge.answer) {
      setCaptchaError(true);
      return;
    }

    setCaptchaError(false);
    setConfigError('');
    setStatus('sending');

    try {
      const moderation = await guardContentSubmission({
        action: 'contact_submit',
        contentType: isBugReport ? 'bug_report' : 'contact',
        texts: [subject, message, steps, expectedBehavior, actualBehavior, pageUrl],
        metadata: {
          contactType,
          path: location.pathname,
          pageUrl,
        },
      });
      showModerationWarning(moderation);

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_CONTACT_TEMPLATE_ID,
        {
          tool_name: selectedType.toolName,
          subject: `${selectedType.subjectPrefix} ${subject.trim()}`,
          name: name.trim() || '\u672a\u5165\u529b',
          from_email: email.trim(),
          message: buildStructuredMessage({
            contactType,
            message,
            pageUrl,
            steps,
            expectedBehavior,
            actualBehavior,
            currentUser,
          }),
        },
        EMAILJS_PUBLIC_KEY,
      );

      setStatus('done');
      setSubject('');
      setMessage('');
      setSteps('');
      setExpectedBehavior('');
      setActualBehavior('');
      setCaptcha('');
      setChallenge(pickChallenge());
    } catch (error) {
      console.error('Contact form send failed:', error);
      setStatus('error');
    }
  };

  return (
    <div className="contact-page">
      <nav className="contact-breadcrumb">
        <Link to="/">{COPY.home}</Link> / <span>{COPY.pageTitle}</span>
      </nav>

      <header className="contact-hero">
        <p className="contact-eyebrow">{COPY.eyebrow}</p>
        <h1 className="contact-title">{COPY.pageTitle}</h1>
        <p className="contact-desc">{COPY.pageDesc}</p>
      </header>

      <section className="contact-card">
        <div className="contact-type-switch" role="tablist" aria-label={COPY.pageTitle}>
          {Object.entries(CONTACT_TYPES).map(([key, value]) => (
            <button
              key={key}
              type="button"
              className={`contact-type-chip ${contactType === key ? 'is-active' : ''}`}
              onClick={() => handleTypeChange(key)}
            >
              <span className="contact-type-chip__label">{value.label}</span>
              <span className="contact-type-chip__desc">{value.description}</span>
            </button>
          ))}
        </div>

        {status === 'done' ? (
          <div className="contact-thanks">
            <p>{COPY.thankYou}</p>
            <button type="button" className="contact-back-link" onClick={resetAfterSuccess}>
              {COPY.sendAnother}
            </button>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="contact-form__grid">
              <div className="contact-form__field">
                <label htmlFor="subject" className="contact-form__label">
                  {COPY.subject} <span className="contact-form__required">必須</span>
                </label>
                <input
                  id="subject"
                  type="text"
                  className="contact-form__input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={
                    contactType === 'bug'
                      ? '\u4f8b: \u30d7\u30ed\u30d5\u30a3\u30fc\u30eb\u4fdd\u5b58\u6642\u306b\u30a8\u30e9\u30fc'
                      : '\u4f8b: \u4f7f\u3044\u65b9\u306b\u3064\u3044\u3066'
                  }
                  required
                />
              </div>

              <div className="contact-form__field">
                <label htmlFor="pageUrl" className="contact-form__label">{COPY.pageUrl}</label>
                <input
                  id="pageUrl"
                  type="text"
                  className="contact-form__input"
                  value={pageUrl}
                  onChange={(e) => setPageUrl(e.target.value)}
                  placeholder={COPY.urlPlaceholder}
                />
              </div>
            </div>

            <div className="contact-form__grid">
              <div className="contact-form__field">
                <label htmlFor="name" className="contact-form__label">{COPY.name}</label>
                <input
                  id="name"
                  type="text"
                  className="contact-form__input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={COPY.optional}
                />
              </div>

              <div className="contact-form__field">
                <label htmlFor="email" className="contact-form__label">
                  {COPY.email}
                  {isInquiry && <span className="contact-form__required">必須</span>}
                </label>
                <input
                  id="email"
                  type="email"
                  className="contact-form__input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={isInquiry ? 'example@email.com' : COPY.optional}
                  required={isInquiry}
                />
              </div>
            </div>

            {isBugReport && (
              <>
                <div className="contact-form__field">
                  <label htmlFor="steps" className="contact-form__label">
                    {COPY.bugSteps} <span className="contact-form__required">必須</span>
                  </label>
                  <textarea
                    id="steps"
                    className="contact-form__textarea"
                    value={steps}
                    onChange={(e) => setSteps(e.target.value)}
                    placeholder={'1. \u30d7\u30ed\u30d5\u30a3\u30fc\u30eb\u3092\u958b\u304f 2. \u753b\u50cf\u3092\u9078\u3076 3. \u4fdd\u5b58\u3092\u62bc\u3059'}
                    rows={4}
                    required={isBugReport}
                  />
                </div>

                <div className="contact-form__grid">
                  <div className="contact-form__field">
                    <label htmlFor="expectedBehavior" className="contact-form__label">{COPY.expected}</label>
                    <textarea
                      id="expectedBehavior"
                      className="contact-form__textarea"
                      value={expectedBehavior}
                      onChange={(e) => setExpectedBehavior(e.target.value)}
                      placeholder={'\u4fdd\u5b58\u304c\u5b8c\u4e86\u3057\u3066\u30d7\u30ed\u30d5\u30a3\u30fc\u30eb\u306b\u623b\u308b'}
                      rows={3}
                    />
                  </div>

                  <div className="contact-form__field">
                    <label htmlFor="actualBehavior" className="contact-form__label">
                      {COPY.actual} <span className="contact-form__required">必須</span>
                    </label>
                    <textarea
                      id="actualBehavior"
                      className="contact-form__textarea"
                      value={actualBehavior}
                      onChange={(e) => setActualBehavior(e.target.value)}
                      placeholder={'400 \u30a8\u30e9\u30fc\u304c\u8868\u793a\u3055\u308c\u3066\u4fdd\u5b58\u3067\u304d\u306a\u3044'}
                      rows={3}
                      required={isBugReport}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="contact-form__field">
              <label htmlFor="message" className="contact-form__label">
                {isBugReport ? COPY.bugBody : COPY.inquiryBody}
                <span className="contact-form__required">必須</span>
              </label>
              <textarea
                id="message"
                className="contact-form__textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  isBugReport
                    ? '\u30a8\u30e9\u30fc\u30e1\u30c3\u30bb\u30fc\u30b8\u3001\u7aef\u672b\u60c5\u5831\u3001\u88dc\u8db3\u60c5\u5831\u306a\u3069'
                    : '\u304a\u554f\u3044\u5408\u308f\u305b\u5185\u5bb9\u3092\u5165\u529b\u3057\u3066\u304f\u3060\u3055\u3044'
                }
                rows={5}
                required
              />
            </div>

            <div className="contact-form__field contact-form__honeypot" aria-hidden="true">
              <label htmlFor="website">website</label>
              <input
                id="website"
                type="text"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                autoComplete="off"
                tabIndex={-1}
              />
            </div>

            <div className="contact-form__field">
              <label htmlFor="captcha" className="contact-form__label">
                {challenge.question}
                <span className="contact-form__required">必須</span>
                <span className="contact-form__hint">{COPY.captchaHint}</span>
              </label>
              <input
                id="captcha"
                type="text"
                className={`contact-form__input${captchaError ? ' contact-form__input--error' : ''}`}
                value={captcha}
                onChange={(e) => {
                  setCaptcha(e.target.value);
                  setCaptchaError(false);
                }}
                placeholder={COPY.captchaPlaceholder}
                autoComplete="off"
              />
              {captchaError && <p className="contact-form__error">{COPY.captchaError}</p>}
            </div>

            {configError && <p className="contact-form__error">{configError}</p>}
            {status === 'error' && <p className="contact-form__error">{COPY.sendFailed}</p>}

            <div className="contact-meta">
              <p>{COPY.currentPath}: <code>{location.pathname}</code></p>
              {currentUser && <p>{COPY.loggedIn}: <code>{currentUser.uid}</code></p>}
            </div>

            <button type="submit" className="contact-form__submit" disabled={status === 'sending'}>
              {status === 'sending' ? COPY.sending : `${selectedType.label}\u3092\u9001\u4fe1`}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}

export default Contact;
